/**
 * Dance Video category — shared constants for the quick tool ("make my photo
 * dance": photo + reference move + song section → motion-transfer render) and
 * the Director dance mode (per-section choreography rendered through the same
 * motion-transfer engine).
 *
 * Imported by client, server and mobile (mobile via a relative path, see the
 * comment in mobile/app/(tabs)/music-video.tsx), so keep this file free of
 * Node/DOM APIs.
 */

/** Credits per billable second of dance output, keyed by resolution. Mirrors
 * `CREDIT_COSTS.DANCE_VIDEO_PER_SECOND` on the server (server/credits.ts).
 * COGS reference (WaveSpeed, 2026-09): Kling 2.6 Std motion-control (the
 * default since Sep 19) $0.042/s, 720p output, billed 3–30 s; Wan 2.2
 * Animate 2 (failover) $0.04/s at 480p and $0.08/s at 720p, billed 3–120 s;
 * Kling 2.6 Pro ≈ $0.112/s; PixVerse V6 reference-to-video with a motion
 * clip ≈ $0.10/s at 720p. */
export const DANCE_VIDEO_RATES = {
  "480p": 5,
  "720p": 10,
} as const;

export type DanceVideoResolution = keyof typeof DANCE_VIDEO_RATES;

/** WaveSpeed bills Wan 2.2 Animate with a 3-second floor. */
export const DANCE_VIDEO_MIN_BILLABLE_SECONDS = 3;
/** One motion-transfer clip is capped like a singing clip; longer songs are
 * rendered per section by the Director dance mode and stitched. */
export const DANCE_VIDEO_MAX_CLIP_SECONDS = 60;
/** Free plan: short, watermarked, 480p. */
export const DANCE_VIDEO_FREE_MAX_SECONDS = 10;
/** Uploaded reference moves longer than this are trimmed before submit. */
export const DANCE_MOTION_REFERENCE_MAX_SECONDS = 30;
/** Time-stretch budget applied to a reference move so its tempo lands on the
 * song's BPM. Beyond ±15 % the dance stops reading as the same choreography. */
export const DANCE_MOTION_STRETCH_MAX_RATIO = 0.15;

export const DANCE_STYLE_IDS = [
  "hip-hop",
  "kpop",
  "house",
  "jazz-funk",
  "waacking",
  "contemporary",
  "ballet",
  "bollywood",
  "anime",
  "basic",
] as const;

export type DanceStyleId = (typeof DANCE_STYLE_IDS)[number];

export const DANCE_STYLES: ReadonlyArray<{
  id: DanceStyleId;
  label: string;
  /** One line the choreography planner and the tool page both show. */
  description: string;
  /** Typical tempo window the style reads well at (used to pick templates). */
  bpmRange: [number, number];
}> = [
  {
    id: "hip-hop",
    label: "Hip-hop",
    description: "Grounded bounce, isolations, sharp accents on the drums.",
    bpmRange: [80, 110],
  },
  {
    id: "kpop",
    label: "K-pop",
    description: "Camera-ready idol choreography with clean formation lines.",
    bpmRange: [100, 135],
  },
  {
    id: "house",
    label: "House",
    description: "Fast footwork and a loose, jacking torso on a four-to-the-floor pulse.",
    bpmRange: [118, 130],
  },
  {
    id: "jazz-funk",
    label: "Jazz-funk",
    description: "Stylised lines, hair and hand vocabulary, big commercial energy.",
    bpmRange: [95, 125],
  },
  {
    id: "waacking",
    label: "Waacking",
    description: "Whipping arm rotations and posing on a disco groove.",
    bpmRange: [110, 130],
  },
  {
    id: "contemporary",
    label: "Contemporary",
    description: "Floor-to-air weight transfers, suspension and release.",
    bpmRange: [60, 100],
  },
  {
    id: "ballet",
    label: "Ballet",
    description: "Turned-out lines, port de bras, controlled turns.",
    bpmRange: [60, 110],
  },
  {
    id: "bollywood",
    label: "Bollywood",
    description: "Expressive hands, hip accents and travelling steps.",
    bpmRange: [100, 140],
  },
  {
    id: "anime",
    label: "Anime dance",
    description: "Idol-style otagei cuteness with exaggerated gestures.",
    bpmRange: [120, 160],
  },
  {
    id: "basic",
    label: "Basic moves",
    description: "Simple two-step, sway and clap patterns for any tempo.",
    bpmRange: [70, 140],
  },
];

export type DanceMotionTemplate = {
  id: string;
  label: string;
  style: DanceStyleId;
  /** Tempo the clip was danced at; the prep step stretches toward the song. */
  bpm: number;
  durationSec: number;
  /** True when the clip starts and ends in a matching neutral pose so it can
   * be looped to cover a longer song section without a visible seam. */
  loopable: boolean;
  /** Portrait (9:16) clip. Wan 2.2 Animate renders in the DRIVING clip's
   * aspect, not the still's, so every template ships in both shapes and
   * `danceMotionTemplateMedia` picks the one matching the output. */
  videoUrl: string;
  posterUrl: string;
  /** Landscape (16:9) clip of the same move. */
  landscapeVideoUrl: string;
  landscapePosterUrl: string;
  license: "solmi-original" | "licensed";
  /** Templates ship in code before their assets are uploaded to R2; the UI
   * only lists the ones flagged available. */
  available: boolean;
  /** Danced by Solmi's faceless motion-capture-suit stand-in, facing the
   * camera the whole take (Oct 1 2026 library). BytePlus takes these as a
   * raw Seedance reference video; the photoreal launch clips trip its
   * real-person filter. Copy-a-dance lists these first. */
  standIn?: boolean;
};

const MOTION_ASSET_BASE = "https://media.solmi.ai/dance-motion";

function template(
  id: string,
  label: string,
  style: DanceStyleId,
  bpm: number,
  durationSec: number,
  loopable: boolean,
  available = false,
): DanceMotionTemplate {
  return {
    id,
    label,
    style,
    bpm,
    durationSec,
    loopable,
    videoUrl: `${MOTION_ASSET_BASE}/${id}.mp4`,
    posterUrl: `${MOTION_ASSET_BASE}/${id}.jpg`,
    landscapeVideoUrl: `${MOTION_ASSET_BASE}/${id}-16x9.mp4`,
    landscapePosterUrl: `${MOTION_ASSET_BASE}/${id}-16x9.jpg`,
    license: "solmi-original",
    available,
  };
}

/**
 * Launch library. Every clip is recorded or licensed by Solmi (one dancer,
 * plain background, 10–30 s) — never a scraped social clip of a real creator.
 * Flip `available` once the mp4 + jpg pair is live under MOTION_ASSET_BASE.
 */
const DANCE_LAUNCH_TEMPLATES: DanceMotionTemplate[] = [
  template("hiphop-bounce-groove", "Bounce groove", "hip-hop", 95, 16, true, true),
  template("hiphop-isolation-hit", "Isolation hits", "hip-hop", 100, 14, true, true),
  template("kpop-point-choreo", "Point choreography", "kpop", 120, 16, false, true),
  template("kpop-formation-walk", "Formation walk", "kpop", 112, 14, true, true),
  template("house-footwork-loop", "Footwork loop", "house", 124, 12, true, true),
  template("jazzfunk-hair-lines", "Hair and lines", "jazz-funk", 108, 15, false, true),
  template("waacking-arm-whips", "Arm whips", "waacking", 122, 11, true, true),
  template("contemporary-release", "Suspension and release", "contemporary", 84, 18, false, true),
  template("ballet-port-de-bras", "Port de bras", "ballet", 76, 16, false, true),
  template("bollywood-hip-accents", "Hip accents", "bollywood", 118, 13, true, true),
  template("anime-idol-wave", "Idol wave", "anime", 138, 9, true, true),
  template("basic-two-step-sway", "Two-step sway", "basic", 100, 10, true, true),
];

/** Faceless suit stand-in, camera-facing (see DanceMotionTemplate.standIn). */
function standInTemplate(
  id: string,
  label: string,
  style: DanceStyleId,
  bpm: number,
  durationSec: number,
  available = false,
): DanceMotionTemplate {
  return { ...template(id, label, style, bpm, durationSec, true, available), standIn: true };
}

/**
 * Copy-a-dance library (Oct 1 2026): two camera-facing routines per style,
 * danced by the suit stand-in on a plain gray cyclorama, 15 s, starting and
 * ending on the same neutral pose (generate-standin-templates.mts). The
 * 9:16 file is a centre crop of the 16:9 take, so both shapes are the same
 * routine. Published Oct 1 2026 after a frame-sheet check of all 20 (faceless
 * suit, camera-facing, full body, one dancer). `bpm` is the prompted tempo:
 * the motion estimator snapped to 60/120/180 on these renders.
 */
export const DANCE_STANDIN_TEMPLATES: DanceMotionTemplate[] = [
  standInTemplate("hiphop-groove-front", "Groove and isolations", "hip-hop", 95, 15, true),
  standInTemplate("hiphop-hits-front", "Hard hits", "hip-hop", 100, 15, true),
  standInTemplate("kpop-point-front", "Point choreography", "kpop", 120, 15, true),
  standInTemplate("kpop-wave-front", "Waves and lines", "kpop", 116, 15, true),
  standInTemplate("house-jack-front", "Jacking footwork", "house", 124, 15, true),
  standInTemplate("house-shuffle-front", "Shuffle steps", "house", 126, 15, true),
  standInTemplate("jazzfunk-lines-front", "Jazz-funk lines", "jazz-funk", 108, 15, true),
  standInTemplate("jazzfunk-strut-front", "Strut and pose", "jazz-funk", 112, 15, true),
  standInTemplate("waacking-whips-front", "Arm whips", "waacking", 122, 15, true),
  standInTemplate("waacking-pose-front", "Pose and roll", "waacking", 118, 15, true),
  standInTemplate("contemporary-flow-front", "Flow and release", "contemporary", 84, 15, true),
  standInTemplate("contemporary-reach-front", "Reach and spiral", "contemporary", 78, 15, true),
  standInTemplate("ballet-arms-front", "Port de bras", "ballet", 76, 15, true),
  standInTemplate("ballet-adagio-front", "Adagio", "ballet", 70, 15, true),
  standInTemplate("bollywood-hands-front", "Hands and hips", "bollywood", 118, 15, true),
  standInTemplate("bollywood-bounce-front", "Bounce and clap", "bollywood", 124, 15, true),
  standInTemplate("anime-idol-front", "Idol routine", "anime", 138, 15, true),
  standInTemplate("anime-cheer-front", "Cheer routine", "anime", 132, 15, true),
  standInTemplate("basic-sway-front", "Easy sway", "basic", 100, 15, true),
  standInTemplate("basic-groove-front", "Easy groove", "basic", 108, 15, true),
];

/** Every template: the launch clips, then the camera-facing stand-ins. */
export const DANCE_MOTION_TEMPLATES: DanceMotionTemplate[] = [
  ...DANCE_LAUNCH_TEMPLATES,
  ...DANCE_STANDIN_TEMPLATES,
];

/** Available stand-in templates for copy-a-dance, optionally one style
 * first (the rest follow so the picker never comes up empty). */
export function danceStandInTemplates(
  style?: string | null,
): DanceMotionTemplate[] {
  const pool = DANCE_STANDIN_TEMPLATES.filter((item) => item.available);
  const styleId = normalizeDanceStyleId(style);
  if (!styleId) return pool;
  return [
    ...pool.filter((item) => item.style === styleId),
    ...pool.filter((item) => item.style !== styleId),
  ];
}

export function availableDanceMotionTemplates(): DanceMotionTemplate[] {
  return DANCE_MOTION_TEMPLATES.filter((item) => item.available);
}

/** Output aspect for a motion-transfer render. Anything that is not
 * landscape (16:9, 21:9, 4:3) drives with the portrait clip. */
export type DanceMotionAspect = "9:16" | "16:9";

export function danceMotionAspect(
  aspectRatio: string | null | undefined,
): DanceMotionAspect {
  const raw = String(aspectRatio ?? "").trim();
  const [w, h] = raw.split(":").map(Number);
  if (Number.isFinite(w) && Number.isFinite(h) && h > 0 && w / h > 1) {
    return "16:9";
  }
  return "9:16";
}

/** The clip + poster of a template in the shape the render will come out
 * in. The driving clip decides the output aspect, so a 16:9 project must
 * drive with the landscape clip or the shot comes back portrait. */
export function danceMotionTemplateMedia(
  template: DanceMotionTemplate,
  aspectRatio: string | null | undefined,
): { videoUrl: string; posterUrl: string; aspect: DanceMotionAspect } {
  const aspect = danceMotionAspect(aspectRatio);
  return aspect === "16:9"
    ? {
        videoUrl: template.landscapeVideoUrl,
        posterUrl: template.landscapePosterUrl,
        aspect,
      }
    : { videoUrl: template.videoUrl, posterUrl: template.posterUrl, aspect };
}

/**
 * Picker demos (Oct 4 2026): the routine performed by one invented dancer
 * through the copy-a-dance path (scripts/dance-motion/template-demo.mts), so
 * users see what they get instead of the black-suit driving clip. 9:16 for
 * every project shape (they are previews; renders use the template clip in
 * the output's shape). Small 360p loops; only ids listed here are published —
 * the CDN caches a 404, so an id is added only after its files are uploaded.
 */
const DANCE_TEMPLATE_DEMO_IDS = new Set<string>([
  "hiphop-groove-front",
  "hiphop-hits-front",
  "kpop-point-front",
  "kpop-wave-front",
  "house-jack-front",
  "house-shuffle-front",
  "jazzfunk-lines-front",
  "jazzfunk-strut-front",
  "waacking-whips-front",
  "waacking-pose-front",
  "contemporary-flow-front",
  "contemporary-reach-front",
  "ballet-arms-front",
  "ballet-adagio-front",
  "bollywood-hands-front",
  "bollywood-bounce-front",
  "anime-idol-front",
  "anime-cheer-front",
  "basic-sway-front",
  "basic-groove-front",
]);
const DANCE_TEMPLATE_DEMO_VERSION = "v1";

export function danceMotionTemplateDemo(
  template: Pick<DanceMotionTemplate, "id">,
): { videoUrl: string; posterUrl: string } | null {
  if (!DANCE_TEMPLATE_DEMO_IDS.has(template.id)) return null;
  const base = `${MOTION_ASSET_BASE}/demo/${template.id}-${DANCE_TEMPLATE_DEMO_VERSION}`;
  // p2: a mid-routine frame (the first frame is the same stance on all 20).
  return { videoUrl: `${base}.mp4`, posterUrl: `${base}-p2.jpg` };
}

/** Driving-clip URL for a template id in the shape of the output, or null
 * when the id is unknown (the caller then falls back to a custom upload). */
export function danceMotionTemplateVideoUrl(
  templateId: string | null | undefined,
  aspectRatio: string | null | undefined,
): string | null {
  const template = getDanceMotionTemplate(templateId);
  return template ? danceMotionTemplateMedia(template, aspectRatio).videoUrl : null;
}

export function getDanceMotionTemplate(
  id: string | null | undefined,
): DanceMotionTemplate | null {
  if (!id) return null;
  return DANCE_MOTION_TEMPLATES.find((item) => item.id === id) ?? null;
}

/** Keyword map from prose a planner (or an older plan) may have written in a
 * style field to the template style id. Order matters: the first hit wins. */
const DANCE_STYLE_KEYWORDS: Array<[DanceStyleId, RegExp]> = [
  ["kpop", /k[\s-]?pop|idol\s+group|comeback|point\s+choreo/i],
  ["anime", /anime|idol\s+wave|otagei|vocaloid/i],
  ["bollywood", /bollywood|bhangra|desi|filmi/i],
  ["ballet", /ballet|pointe|port\s+de\s+bras|classical\s+dance/i],
  ["waacking", /waack|punking|voguing|vogue/i],
  ["jazz-funk", /jazz[\s-]?funk|heels|commercial\s+jazz/i],
  ["house", /\bhouse\b|footwork|jacking/i],
  ["contemporary", /contemporary|lyrical|modern\s+dance|release/i],
  ["hip-hop", /hip[\s-]?hop|street|urban|breaking|popping|locking|krump/i],
  ["basic", /basic|simple|beginner|two[\s-]?step|sway|casual/i],
];

/**
 * Resolve any style text to a template style id. Exact ids pass through;
 * prose like "K-pop commercial dance" or "Street hip hop groove" maps by
 * keyword; unknown text returns null so the caller can fall back. Older
 * choreography plans stored prose here (x_Qf9oM7 Sep 8 2026: "K-pop
 * commercial dance" matched no style, so every shot took the closest-tempo
 * clip across all styles — house footwork on a K-pop arena stage).
 */
export function normalizeDanceStyleId(
  style: string | null | undefined,
): DanceStyleId | null {
  const raw = String(style ?? "").trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const exact = DANCE_STYLE_IDS.find((id) => id === lower);
  if (exact) return exact;
  for (const [id, re] of DANCE_STYLE_KEYWORDS) {
    if (re.test(raw)) return id;
  }
  return null;
}

/**
 * Pick the template that best fits a style and tempo. Prefers same-style
 * clips whose BPM is within the stretch budget; falls back to the closest
 * tempo across all available templates so a section always gets a move.
 * `variant` rotates through the same-style candidates (by tempo rank) so
 * consecutive shots in one section do not all repeat one clip.
 */
export function pickDanceMotionTemplate(
  style: string | null | undefined,
  bpm: number | null | undefined,
  pool: DanceMotionTemplate[] = availableDanceMotionTemplates(),
  variant = 0,
): DanceMotionTemplate | null {
  if (pool.length === 0) return null;
  const styleId = normalizeDanceStyleId(style);
  const target = Number(bpm);
  const scored = pool
    .map((item) => {
      const tempoDistance = Number.isFinite(target) && target > 0
        ? Math.abs(Math.log(item.bpm / target))
        : 0;
      const styleMatch = styleId && item.style === styleId ? 0 : 1;
      return { item, score: styleMatch * 10 + tempoDistance, styleMatch };
    })
    .sort((a, b) => a.score - b.score);
  const sameStyle = scored.filter((s) => s.styleMatch === 0);
  const rotation = sameStyle.length > 1 ? sameStyle : scored;
  const index =
    ((Math.floor(Number(variant) || 0) % rotation.length) + rotation.length) %
    rotation.length;
  return rotation[index]?.item ?? scored[0]?.item ?? null;
}

/** Stretch ratio (output speed multiplier) that moves a reference clip's tempo
 * onto the song's, clamped to the budget. 1 means untouched. */
export function danceMotionStretchRatio(
  sourceBpm: number | null | undefined,
  targetBpm: number | null | undefined,
): number {
  const source = Number(sourceBpm);
  const target = Number(targetBpm);
  if (!Number.isFinite(source) || !Number.isFinite(target)) return 1;
  if (source <= 0 || target <= 0) return 1;
  // Dance tempo is ambiguous by a factor of two (half-time / double-time);
  // pick whichever octave of the source lands nearest the target.
  let ratio = target / source;
  while (ratio > 1.5) ratio /= 2;
  while (ratio < 0.66) ratio *= 2;
  const min = 1 - DANCE_MOTION_STRETCH_MAX_RATIO;
  const max = 1 + DANCE_MOTION_STRETCH_MAX_RATIO;
  return Math.min(max, Math.max(min, ratio));
}

export function danceVideoRate(resolution?: string | null): number {
  const key = String(resolution || "480p").toLowerCase();
  return key === "720p" ? DANCE_VIDEO_RATES["720p"] : DANCE_VIDEO_RATES["480p"];
}

export function danceVideoBillableSeconds(seconds: number): number {
  const ceil = Math.ceil(Number(seconds));
  return Math.max(
    DANCE_VIDEO_MIN_BILLABLE_SECONDS,
    Number.isFinite(ceil) && ceil > 0 ? ceil : DANCE_VIDEO_MIN_BILLABLE_SECONDS,
  );
}
